import { _ } from "../polyglot/polyglot.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import keyFigurePresetsManagerWidget from "../widgets/keyFigurePresetsManagerWidget.js";
import openDriverKeyFigureCreationDialog from "../widgets/keyFigures/openDriverKeyFigureCreationDialog.js";
import TabWidget from "../widgets/TabWidget/TabWidget.js";
import VisibleKeyFigureListWidget from "../widgets/VisibleKeyFigureListWidget/VisibleKeyFigureListWidget.js";

/**
 * `keyFigureSettingsSectionWidget` is used to edit the key figures
 * and the key figure presets. It renders a tab with financial key
 * figures and another tab with key figure presets.
 *
 * @param {boolean} [canEditKeyFigures = false] - When true, render a link to edit each
 * keyfigure. Setting it to true  only makes sense when used within the Company
 * Settings document, as the KeyFigure editor is only available in Accounting2.
 * @param {function} [viewKeyFigureCallback] - Function taking a key
 * figure as argument that is executed when the view button is clicked.
 * @param {function} [onKeyFigureCreated] - Function taking a key
 * figure as argument that is executed when the user has requested the
 * creation of a new key figure.
 * @param {function} [isKeyFigureEnabled] - Called with a key figure
 * as argument. The return value represents the state of the
 * enable/disable toggle. This function must be provided for the
 * enable/disable column to be visible.
 * @param {function} [setKeyFigureEnabled] - Called with a key figure
 * and a boolean as argument. The boolean is true if the key figure
 * must be enabled.
 * @param {function} [canDisableKeyFigure] - Called with a key figure
 * as argument. Must return true for the toggle to appear for this key
 * figure.
 * @param {boolean} [canImportCustomKeyFigure] - When true, an Import button
 * is displayed allowing to import a custom key figure.
 * @param {OwnKeyFigureSettingsService} ownKeyFigureSettingsService - Service handling custom
 * key figures.
 */
export default class KeyFigureSettingsDocumentSection extends Widget {
	constructor({
		onKeyFigureCreated,
		ownKeyFigureSettingsService,
		canEditKeyFigures = () => false,
		canDeleteKeyFigure,
		isKeyFigureEnabled,
		setKeyFigureEnabled,
		canDisableKeyFigure,
		viewKeyFigureCallback,
		canImportCustomKeyFigure,
		redirectToKeyFigureEditor,
	} = {}) {
		super(...arguments);

		this._presets = [];
		this._allKeyFigures = [];

		this._visiblePreset = undefined;
		this._onKeyFigureCreated = onKeyFigureCreated;

		this.onVisibleKeyFiguresChange = this._events.createEvent();
		this.onPresetCreated = this._events.createEvent();
		this.onPresetClicked = this._events.createEvent();
		this.customKeyFigureRemoved = this._events.createEvent();
		this._ownKeyFigureService = ownKeyFigureSettingsService;

		const changeVisibleKeyFiguresCallback = () =>
			this._changeVisibleKeyFigures();

		const removeKeyFigureCallback = (...rest) =>
			this.customKeyFigureRemoved.trigger(...rest);

		const editKeyFigureCallback = (keyFigure) =>
			redirectToKeyFigureEditor(keyFigure);

		const customKPI = canImportCustomKeyFigure &&
			ownKeyFigureSettingsService && {
				createNewCustomKeyFigure: (modelData) =>
					this._ownKeyFigureService.create({
						modelData,
					}),
				updateExistingKeyFigure: (modelData) =>
					this._ownKeyFigureService.save({
						modelData,
					}),
				canDeleteKeyFigure: (keyFigure) =>
					this._ownKeyFigureService.canDelete(keyFigure),
				canRestoreKeyFigure: (keyFigure) =>
					this._ownKeyFigureService.canRestore(keyFigure),
				canUploadKeyFigure: (keyFigure) =>
					this._ownKeyFigureService.canUpload(keyFigure),
				canDownloadKeyFigure: (keyFigure) =>
					this._ownKeyFigureService.canDownload(keyFigure),
				getDownloadUrlForKeyFigure: (keyFigure) =>
					this._ownKeyFigureService.getDownloadUrlFor(keyFigure),
				restoreKeyFigure: (keyFigure) =>
					this._ownKeyFigureService.delete({
						modelId: keyFigure.getId(),
					}),
			};

		this._financialKeyFigureList = new VisibleKeyFigureListWidget({
			customKPI,
			changeVisibleKeyFiguresCallback,
			editKeyFigureCallback,
			canEditKeyFigures,
			createKeyFigureCallback: () => redirectToKeyFigureEditor(),
			removeKeyFigureCallback,
			canDeleteKeyFigure,
			viewKeyFigureCallback,
			isKeyFigureEnabled,
			setKeyFigureEnabled,
			canDisableKeyFigure,
		});

		this._nonFinancialKeyFigureList = new VisibleKeyFigureListWidget({
			ownKeyFigureSettingsService,
			changeVisibleKeyFiguresCallback,
			editKeyFigureCallback,
			canEditKeyFigures,
			createKeyFigureCallback:
				this._onKeyFigureCreated &&
				(() =>
					openDriverKeyFigureCreationDialog({
						onKeyFigureCreated: this._onKeyFigureCreated.bind(this),
					})),
			customKPI,
			removeKeyFigureCallback,
			canDeleteKeyFigure,
			viewKeyFigureCallback,
			isKeyFigureEnabled,
			setKeyFigureEnabled,
			canDisableKeyFigure,
		});

		this._keyFigurePresetsManagerWidget = keyFigurePresetsManagerWidget({
			presets: this._presets,
			canEditKeyFigures,
		});

		this._keyFigurePresetsManagerWidget.onPresetCreated.register(
			this.onPresetCreated.trigger,
		);

		this._keyFigurePresetsManagerWidget.onPresetClicked.register(
			this.onPresetClicked.trigger,
		);

		this._tabsWidget = this._createTabsWidget();
	}

	setAllKeyFigures(newKeyFigures) {
		this._allKeyFigures = newKeyFigures;
		this._updateVisibleKeyFigures();
	}

	setPresets(newPresets) {
		this._presets = newPresets
			.filter((preset) => !preset.isVisibleKeyFigures())
			.sort((a, b) => (a.getName() > b.getName() ? 1 : -1));

		this._keyFigurePresetsManagerWidget.setPresets(this._presets);

		const visiblePreset = newPresets.find((preset) =>
			preset.isVisibleKeyFigures(),
		);
		this.setVisiblePreset(visiblePreset);
	}

	addKeyFigure(keyFigure) {
		this._visiblePreset.addKeyFigure(keyFigure);
		this.setVisiblePreset(this._visiblePreset);
		return this._visiblePreset;
	}

	renderContentOn(html) {
		html.div(
			{
				class: "col col-md-12 col-lg-10 col-lg-offset-1",
			},
			this._tabsWidget,
		);
	}

	setVisiblePreset(visiblePreset) {
		this._visiblePreset = visiblePreset;

		if (!visiblePreset) {
			throw new Error(
				"There should always be a VisibleKeyFigures preset",
			);
		}

		this._updateVisibleKeyFigures();
	}

	_updateVisibleKeyFigures() {
		if (!this._visiblePreset) {
			return;
		}

		const allKeyFigures = this._allKeyFigures;

		const financialKeyFigures = allKeyFigures.filter((keyFigure) =>
			keyFigure.isFinancialKeyFigure(),
		);

		const nonFinancialKeyFigures = allKeyFigures.filter((keyFigure) =>
			keyFigure.isNonFinancialKeyFigure(),
		);

		this._setKeyFiguresInListWidget({
			listWidget: this._financialKeyFigureList,
			keyFigures: financialKeyFigures,
		});

		this._setKeyFiguresInListWidget({
			listWidget: this._nonFinancialKeyFigureList,
			keyFigures: nonFinancialKeyFigures,
		});
	}

	_setKeyFiguresInListWidget({ listWidget, keyFigures }) {
		const isVisible = (keyFigure) =>
			this._visiblePreset.containsKeyFigure(keyFigure);

		const isInvisible = (keyFigure) => !isVisible(keyFigure);

		listWidget.setKeyFigures({
			visibleKeyFigures: keyFigures.filter(isVisible),
			invisibleKeyFigures: keyFigures.filter(isInvisible),
		});
	}

	_createTabsWidget() {
		return new TabWidget({
			subTabs: true,
			tabs: [
				{
					name: _("Financial KPIs"),
					id: "financial-kpis",
					dataTestName: "financial-kpis",
					content: this._financialKeyFigureList,
				},
				{
					name: _("Driver KPIs"),
					id: "driver-kpis",
					dataTestName: "driver-kpis",
					content: this._nonFinancialKeyFigureList,
				},
				{
					name: _("Presets"),
					id: "kpi-presets",
					dataTestName: "kpi-presets",
					content: this._keyFigurePresetsManagerWidget,
				},
			],
			tabsOnlyOnMobile: false,
			hideLabelsOnMobile: false,
		});
	}

	_changeVisibleKeyFigures() {
		this._visiblePreset.setKeyFigures([
			...this._financialKeyFigureList.getVisibleKeyFigures(),
			...this._nonFinancialKeyFigureList.getVisibleKeyFigures(),
		]);

		this.onVisibleKeyFiguresChange.trigger(this._visiblePreset);
	}
}
